import { Bell } from "lucide-react";
import { useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import logging from "../utils/logging";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatTime(isoStr) {
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NotificationBell({ notifications = [], onRead }) {
  const unreadCount = notifications.filter((n) => !n.read).length;
  const navigate = useNavigate();

  const handleMarkRead = useCallback(async (notifId) => {
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      if (onRead) onRead();
    } catch (err) {
      logging.error("Failed to mark notification read", err);
    }
  }, [onRead]);

  // Tap a notification → mark it read AND deep-link to the underlying alert
  // on the dashboard. The Dashboard's `?alert=<id>` handler will fly the map
  // to the location, open the popup, and highlight the feed card.
  const handleNotifClick = useCallback((notif) => {
    if (!notif.read) handleMarkRead(notif.notification_id);
    if (notif.alert_id) {
      navigate(`/dashboard?alert=${encodeURIComponent(notif.alert_id)}`);
    }
  }, [handleMarkRead, navigate]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative rounded-sm text-gray-600 hover:text-black hover:bg-gray-100"
          data-testid="notification-bell"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="notification-badge absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 rounded-sm" data-testid="notification-dropdown">
        <div className="px-3 py-2 border-b border-gray-200">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500">
            Notifications ({unreadCount})
          </p>
        </div>
        {notifications.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-xs text-gray-400 font-body">No notifications</p>
          </div>
        ) : (
          notifications.slice(0, 10).map((notif) => (
            <DropdownMenuItem
              key={notif.notification_id}
              className={`px-3 py-2.5 cursor-pointer rounded-none focus:bg-gray-50 ${
                notif.read ? "opacity-60" : "bg-red-50/50"
              }`}
              data-testid={`notification-item-${notif.notification_id}`}
              onClick={() => handleNotifClick(notif)}
            >
              <div className="flex flex-col gap-0.5 w-full">
                <p className="text-sm text-gray-800 font-body">{notif.message}</p>
                <p className="text-[10px] text-gray-400 font-body">{formatTime(notif.created_at)}</p>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
